import { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { authHeaders } from "@/lib/auth";
import { Send, X, Circle, Download, Search } from "lucide-react";

const POLL_MS = 2500;

/**
 * Conversations tab — polling-based live chat inbox.
 * Admin-only (parent renders this only when user.role === "admin").
 */
export function ConversationsTab() {
  const [chats, setChats] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [note, setNote] = useState("");
  const notifiedRef = useRef(new Set());

  const loadChats = async () => {
    try {
      const { data } = await api.get("/admin/chats", authHeaders());
      setChats(data.items || []);
      // Browser notification for new chats we haven't seen.
      if ("Notification" in window && Notification.permission === "granted") {
        for (const c of data.items || []) {
          if (c.status === "new" && !notifiedRef.current.has(c.id)) {
            notifiedRef.current.add(c.id);
            try { new Notification(`New chat — ${c.name}`, { body: c.first_message }); } catch (_) {}
          }
        }
      }
    } catch (_) {}
  };

  const loadDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/chats/${id}`, authHeaders());
      setDetail(data);
    } catch (_) {}
  };

  useEffect(() => {
    loadChats();
    if ("Notification" in window && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch (_) {}
    }
    const id = setInterval(loadChats, POLL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!selected) return;
    loadDetail(selected);
    const id = setInterval(() => loadDetail(selected), POLL_MS);
    return () => clearInterval(id);
  }, [selected]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !selected) return;
    await api.post(`/admin/chats/${selected}/reply`, { body: reply.trim() }, authHeaders());
    setReply("");
    loadDetail(selected);
  };

  const addNote = async () => {
    if (!note.trim() || !selected) return;
    await api.post(`/admin/chats/${selected}/notes`, { body: note.trim() }, authHeaders());
    setNote("");
    loadDetail(selected);
  };

  const changeStatus = async (status) => {
    if (!selected) return;
    await api.patch(`/admin/chats/${selected}`, { status }, authHeaders());
    loadChats(); loadDetail(selected);
  };

  const statusCls = { new: "bg-wafa-sunshine/40 text-wafa-deep", open: "bg-wafa-primary/10 text-wafa-primary", closed: "bg-wafa-ink/10 text-wafa-ink/70" };

  return (
    <div className="mt-8 grid gap-4 lg:grid-cols-3" data-testid="conversations-tab">
      <div className="rounded-lg border border-wafa-deep/10 bg-white lg:col-span-1">
        <div className="border-b border-wafa-deep/10 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-wafa-deep">Chats ({chats.length})</div>
        <div className="max-h-[560px] divide-y divide-wafa-deep/10 overflow-y-auto">
          {chats.map((c) => (
            <button key={c.id} onClick={() => setSelected(c.id)} data-testid={`chat-row-${c.id}`} className={`flex w-full items-start justify-between gap-2 px-4 py-3 text-left hover:bg-wafa-cream/30 ${selected === c.id ? "bg-wafa-cream/40" : ""}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="truncate text-sm font-semibold text-wafa-deep">{c.name}</div>
                  {c.unread_admin_count > 0 ? <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-wafa-primary px-1 text-[10px] font-bold text-white">{c.unread_admin_count}</span> : null}
                </div>
                <div className="truncate text-xs text-wafa-ink/60">{c.email}</div>
                <div className="mt-1 line-clamp-2 text-xs text-wafa-ink/70">{c.first_message}</div>
              </div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${statusCls[c.status] || ""}`}>{c.status}</span>
            </button>
          ))}
          {chats.length === 0 ? <div className="px-4 py-8 text-center text-sm text-wafa-ink/60">No chats yet.</div> : null}
        </div>
      </div>

      <div className="rounded-lg border border-wafa-deep/10 bg-white lg:col-span-2">
        {!detail ? (
          <div className="p-8 text-center text-sm text-wafa-ink/60">Select a chat to view.</div>
        ) : (
          <div className="flex h-[600px] flex-col">
            <div className="flex items-center justify-between border-b border-wafa-deep/10 px-4 py-3">
              <div>
                <div className="font-semibold text-wafa-deep">{detail.chat.name}</div>
                <div className="text-xs text-wafa-ink/60">
                  <a href={`mailto:${detail.chat.email}`} className="hover:text-wafa-primary">{detail.chat.email}</a>
                  {detail.chat.phone ? ` · ${detail.chat.phone}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select value={detail.chat.status} onChange={(e) => changeStatus(e.target.value)} data-testid="chat-detail-status" className="h-8 rounded-md border border-wafa-deep/10 px-2 text-xs">
                  <option value="new">new</option><option value="open">open</option><option value="closed">closed</option>
                </select>
              </div>
            </div>

            <div className="flex-1 space-y-2 overflow-y-auto bg-wafa-cream/20 p-4" data-testid="chat-detail-messages">
              {detail.messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender === "agent" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${m.sender === "agent" ? "bg-wafa-primary text-white" : "bg-white border border-wafa-deep/10 text-wafa-deep"}`}>
                    {m.body}
                    <div className="mt-1 text-[9px] opacity-70">{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>

            {detail.notes?.length ? (
              <div className="border-t border-wafa-deep/10 bg-wafa-sunshine/10 px-4 py-2 text-[11px] text-wafa-deep">
                <b>Internal notes:</b> {detail.notes.map((n) => n.body).join(" · ")}
              </div>
            ) : null}

            <form onSubmit={sendReply} className="flex items-center gap-2 border-t border-wafa-deep/10 p-2" data-testid="chat-detail-reply-form">
              <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type reply…" data-testid="chat-detail-reply-input" className="h-10 flex-1 rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary" />
              <button type="submit" data-testid="chat-detail-reply-send" className="inline-flex h-10 items-center gap-1 rounded-md bg-wafa-primary px-3 text-sm font-semibold text-white hover:bg-wafa-deep">
                <Send className="h-4 w-4" /> Send
              </button>
            </form>

            <div className="flex items-center gap-2 border-t border-wafa-deep/10 p-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note (visitor never sees this)…" data-testid="chat-detail-note-input" className="h-9 flex-1 rounded-md border border-wafa-sunshine/40 bg-wafa-sunshine/10 px-3 text-xs focus:outline-none" />
              <button type="button" onClick={addNote} data-testid="chat-detail-note-add" className="rounded-md border border-wafa-deep/15 px-3 py-1.5 text-xs font-semibold text-wafa-deep hover:bg-wafa-cream/40">Add note</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * All Enquiries tab — every source in one list, with expand, filter,
 * date range, search and CSV export.
 */
export function AllEnquiriesTab({ role }) {
  const [items, setItems] = useState([]);
  const [source, setSource] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = () => {
    const p = {};
    if (source !== "all") p.source = source;
    if (fromDate) p.q_from = `${fromDate}T00:00:00`;
    if (toDate) p.q_to = `${toDate}T23:59:59`;
    if (search.trim()) p.search = search.trim();
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/enquiries", { ...authHeaders(), params: params() });
      setItems(data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [source, fromDate, toDate]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const s = search.trim().toLowerCase();
    return items.filter((i) => JSON.stringify(i).toLowerCase().includes(s));
  }, [items, search]);

  const sources = role === "careers"
    ? ["all", "careers-application"]
    : ["all", "contact_submissions", "tool_pdpl", "tool_iso27001", "tool_software_cost", "tool_ai_opportunity", "careers", "careers-application", "live-chat"];

  const openDetail = async (id) => {
    if (expanded === id) { setExpanded(null); setDetail(null); return; }
    setExpanded(id); setDetail(null);
    try {
      const { data } = await api.get(`/admin/enquiries/${id}`, authHeaders());
      setDetail(data);
    } catch (_) {}
  };

  const exportCsv = () => {
    const t = localStorage.getItem("wafa.admin.token");
    const qs = new URLSearchParams(params()).toString();
    fetch(`${API}/admin/enquiries.csv?${qs}`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "enquiries.csv"; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      });
  };

  const src = (s) => ({
    contact_submissions: "Contact",
    tool_pdpl: "PDPL", tool_iso27001: "ISO 27001", tool_software_cost: "Software Cost",
    tool_ai_opportunity: "AI Opportunity", careers: "Careers (open CV)", "careers-application": "Job application", "live-chat": "Live chat",
  }[s] || (s?.startsWith("download-") ? "Download · " + s.replace("download-", "").replace(/-/g, " ") : s));

  return (
    <div className="mt-8" data-testid="enquiries-tab">
      <div className="flex flex-wrap items-center gap-3">
        <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="enq-filter-source" className="h-10 rounded-md border border-wafa-deep/10 bg-white px-3 text-sm">
          {sources.map((s) => <option key={s} value={s}>{s === "all" ? "All sources" : src(s)}</option>)}
        </select>
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="enq-filter-from" className="h-10 rounded-md border border-wafa-deep/10 bg-white px-3 text-sm" />
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="enq-filter-to" className="h-10 rounded-md border border-wafa-deep/10 bg-white px-3 text-sm" />
        <div className="flex items-center gap-2 rounded-md border border-wafa-deep/10 bg-white px-3">
          <Search className="h-4 w-4 text-wafa-ink/50" />
          <input placeholder="Name, email, company…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="enq-search" className="h-10 w-56 bg-transparent text-sm focus:outline-none" />
        </div>
        <button onClick={exportCsv} data-testid="enq-export-csv" className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-wafa-primary px-4 py-2 text-sm font-semibold text-white hover:bg-wafa-deep">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <div className="mt-6 overflow-hidden rounded-lg border border-wafa-deep/10 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-wafa-cream/40 text-left text-xs uppercase tracking-wider text-wafa-deep">
            <tr>
              <th className="px-4 py-3">Date</th><th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Name / Email</th><th className="px-4 py-3">Company</th>
              <th className="px-4 py-3">Preview</th><th className="px-4 py-3">Email</th>
              <th className="px-4 py-3 text-right">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-wafa-deep/10">
            {filtered.map((l) => {
              const company = l.company || (l.payload && l.payload.company) || "—";
              const isOpen = expanded === l.id;
              return (
                <>
                  <tr key={l.id} data-testid={`enq-row-${l.id}`}>
                    <td className="px-4 py-3 text-xs text-wafa-ink/60">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3"><span className="rounded-full bg-wafa-cream px-2 py-0.5 text-[10px] font-semibold uppercase text-wafa-deep">{src(l.source)}</span></td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-wafa-deep">{l.name || l.first_name || "—"}</div>
                      <div className="text-xs text-wafa-ink/60">{l.email}</div>
                    </td>
                    <td className="px-4 py-3 text-wafa-ink/80">{company}</td>
                    <td className="px-4 py-3 text-xs text-wafa-ink/70">{l.preview || "—"}</td>
                    <td className="px-4 py-3 text-xs">{l.email_status || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openDetail(l.id)} data-testid={`enq-toggle-${l.id}`} className="text-xs font-semibold text-wafa-deep hover:text-wafa-primary">
                        {isOpen ? "Hide" : "View"}
                      </button>
                    </td>
                  </tr>
                  {isOpen ? (
                    <tr>
                      <td colSpan={7} className="bg-wafa-cream/20 px-4 py-4">
                        {!detail ? <div className="text-xs text-wafa-ink/60">Loading…</div> : (
                          <div className="space-y-3">
                            <pre className="max-w-full overflow-auto rounded bg-white p-3 text-[11px] leading-relaxed text-wafa-ink/80">
{JSON.stringify(detail.lead, null, 2)}
                            </pre>
                            {detail.messages ? (
                              <div className="rounded bg-white p-3">
                                <div className="text-[11px] font-semibold uppercase tracking-wider text-wafa-deep">Transcript</div>
                                <div className="mt-2 space-y-1">
                                  {detail.messages.map((m) => (
                                    <div key={m.id} className="text-xs">
                                      <span className="font-semibold text-wafa-deep">{m.sender}:</span> {m.body}
                                      <span className="ml-2 text-[10px] text-wafa-ink/50">{new Date(m.created_at).toLocaleTimeString()}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : null}
                            {detail.application ? (
                              <div className="rounded bg-white p-3 text-xs">
                                <b>CV:</b>{" "}
                                <a href="#" onClick={(e) => {
                                  e.preventDefault();
                                  const t = localStorage.getItem("wafa.admin.token");
                                  fetch(`${API}/admin/applications/${detail.application.id}/cv`, { headers: { Authorization: `Bearer ${t}` } })
                                    .then((r) => r.blob()).then((blob) => {
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement("a");
                                      a.href = url; a.download = detail.application.cv_filename || "cv"; a.click();
                                    });
                                }} className="text-wafa-primary hover:text-wafa-deep">{detail.application.cv_filename}</a>
                              </div>
                            ) : null}
                          </div>
                        )}
                      </td>
                    </tr>
                  ) : null}
                </>
              );
            })}
            {!loading && filtered.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-wafa-ink/60">No enquiries.</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
